import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalICD10AddCode from "../chatEdit/modals/ModalICD10AddCode";
import { normalizList, sortByCode, hasModifier, getCpt2GroupHeader } from "../../utils/utils";
import { isNonBillableList } from "../../utils/notBilled";
import { displayUnits } from "../../utils/billingRules";
import ModalEditContextVariable from "../chatEdit/modals/ModalEditContextVariable";
import { useChats } from "../../hooks/useChats";
import useDragAndDropList from "../../hooks/useDragAndDropList";

export default function TableViewICD10({ value, setValue, initValue, chat, showAdd, renderMic, type }) {
  const { t } = useTranslation();
  const [showAddIcd10Modal, setShowAddIcd10Modal] = useState(false);
  const [init, setInit] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const highlightTimer = useRef(null);
  const { showEditContextVariable } = useChats();

  const listContainerRef = useRef(null);
  const isDraggable = chat.status !== "archived" && chat.status !== "invalid" && type !== "cpt2codes";

  const { draggedIndex, dragOverIndex, handleDragStart, handleDragOver, handleDrop, handleDragEnd, capturePositions } =
    useDragAndDropList(value || [], setValue, undefined, listContainerRef);

  const moveToTop = (i) => {
    if (i === 0) return;
    capturePositions();
    const updated = [...value];
    const [item] = updated.splice(i, 1);
    updated.unshift(item);
    setValue(updated);
    setHighlightedIndex(0);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightedIndex(null), 800);
  };

  useEffect(() => {
    return () => clearTimeout(highlightTimer.current);
  }, []);

  useEffect(() => {
    // A code list stored as a JSON string normalizes to rows like any other
    // shape (see normalizList); only a string with no codes in it stays raw,
    // which leaves the column empty exactly as before.
    const list = normalizList(initValue);
    if (typeof initValue === "string" && list.length === 0) {
      return setValue(initValue);
    }

    if (type === "cpt2codes") list.sort(sortByCode);
    setValue(list);
    setInit(true);
    // eslint-disable-next-line
  }, []);

  const removeElement = async (i) => {
    const updated = [...value];
    updated.splice(i, 1);
    setValue(updated);
  };

  return (
    <div className="icd-10-wrapper">
      <div className="icd-10-list" ref={listContainerRef}>
        {init &&
          value.flatMap((elem, i) => {
            const groupHeader = type === "cpt2codes" || isNonBillableList(type) ? getCpt2GroupHeader(value, i, type) : null;
            const isDragging = draggedIndex === i;
            const isDragOver = dragOverIndex === i;
            const isHighlighted = highlightedIndex === i;

            const itemClassName = [
              "icd-10-item",
              isDraggable ? "draggable" : "",
              isDragging ? "dragging" : "",
              isDragOver ? "drag-over" : "",
            ]
              .filter(Boolean)
              .join(" ");

            const badgeClassName = ["badge", "gray", isDraggable ? "clickable" : "", isHighlighted ? "highlighted" : ""]
              .filter(Boolean)
              .join(" ");

            const items = [];
            if (groupHeader) {
              items.push(
                <div key={"header_" + i} className="icd-10-group-header">
                  {groupHeader}
                </div>
              );
            }
            items.push(
              <div
                className={itemClassName}
                key={elem.code || "code_" + i}
                data-item-id={elem.code || "code_" + i}
                draggable={isDraggable}
                onDragStart={isDraggable ? handleDragStart(i) : undefined}
                onDragOver={isDraggable ? handleDragOver(i) : undefined}
                onDrop={isDraggable ? handleDrop(i) : undefined}
                onDragEnd={isDraggable ? handleDragEnd : undefined}
              >
                {isDraggable && <span className="material-symbols-outlined drag-handle">drag_indicator</span>}
                <div className="icd-10-code">
                  <div className={badgeClassName} onClick={isDraggable ? () => moveToTop(i) : undefined}>
                    {elem.code}
                  </div>
                </div>
                {hasModifier(type, elem?.code) && (
                  <div className="icd-10-modifier">x {isNonBillableList(type) ? displayUnits(elem) : elem?.modifier || 1}</div>
                )}
                <div className="icd-10-desc">{elem.description}</div>
                <div className="icd-10-action">
                  {chat.status !== "archived" && chat.status !== "invalid" && (
                    <button className="small white no-border round">
                      <i className="notranslate material-icons" onClick={() => removeElement(i)}>
                        delete
                      </i>
                    </button>
                  )}
                </div>
              </div>
            );
            return items;
          })}
      </div>

      {renderMic && !showAdd && <div className="mt-30">{renderMic()}</div>}

      {showAdd && (
        <>
          <div className="icd-10-add mt-30 vertical-align">
            <button className="small white" onClick={() => setShowAddIcd10Modal("icd10")}>
              <i className="notranslate material-icons">add</i> {t("tableView.icd10.addCode")}
            </button>
            {renderMic && renderMic()}
          </div>

          {showAddIcd10Modal && (
            <ModalICD10AddCode
              section={showAddIcd10Modal}
              type={type}
              close={() => {
                setShowAddIcd10Modal(false);
              }}
              onSave={(codes) => {
                // Handle the saved codes
                setValue(codes);
              }}
              initialCodes={value}
            ></ModalICD10AddCode>
          )}
        </>
      )}

      {showEditContextVariable && (
        <ModalEditContextVariable
          onSave={(result) => {
            const updated = [...(value || [])];
            const items = Array.isArray(result) ? result : [result];
            for (const val of items) {
              updated.push({
                code: val.split("-")[0],
                description: val.replace(val.split("-")[0] + "- ", ""),
              });
            }
            setValue(updated);
          }}
        ></ModalEditContextVariable>
      )}
    </div>
  );
}
